import { useEffect, useRef, useState } from "react";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import {
	useDeleteDisciplinaryAction,
	useGetDisciplinaryAction,
	useGetDisciplinaryActionBySearch,
} from "../../../../services/disciplinary-action.service";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import { DisciplinaryActionDto } from "../../../../models/disciplinary-action.dto";
import { AiOutlinePlus } from "react-icons/ai";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { useGetDisciplinaryActionType } from "../../../../services/disciplinary-action-type.service";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetGrievanceReport } from "../../../../services/grievance-report.service";
import DisciplinaryModalForm from "../modals/DisciplinaryModalForm";
import APISearchInput from "../../../../components/search/APISearchInput";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import {
	disciplinaryActionsColumns,
	disciplinaryActionsColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";
import ExportDisciplinaryActionsModal from "../modals/ExportDisciplinaryActionsModal";
import { GoUpload } from "react-icons/go";
import { BsDownload } from "react-icons/bs";
import { ChevronDown, Upload } from "lucide-react";
import {
	PERMISSIONS,
	PermissionChecker,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";

const DisciplinaryActionsView = () => {
	const { currentCompany } = useCompanyStore();
	const { currentUser, userType } = useUserStore();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(disciplinaryActionsColumnVisibility);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const { checkUserPermissionsOr } = useCheckUserPermissions();
	// Only conduct managers may create/update/delete disciplinary actions
	// Same rule as payroll-hr: HR or Operations users with the conduct permission
	const canManageConduct =
		["HR", "OPERATIONS"].includes(currentUser?.user?.category as string) &&
		checkUserPermissionsOr([PERMISSIONS.COMPANY_CONFIGS_CONDUCT_WRITE]);

	const { mutate: searchDisciplinaryActions } =
		useGetDisciplinaryActionBySearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<DisciplinaryActionDto>({
		fetchHook: useGetDisciplinaryAction,
		options: { companyId: currentCompany?.id as number },
	});

	// Fetched data for select fields requiring data from database
	const options = {
		companyId: currentCompany?.id as number,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "createdAt:desc" as any,
	};
	const { data: employees } = useGetEmployee({
		...options,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: disciplinaryActionTypes } =
		useGetDisciplinaryActionType(options);
	const { data: grievanceReports } = useGetGrievanceReport(options);

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteDisciplinaryAction } = useDeleteDisciplinaryAction();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteDisciplinaryAction(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const handleOpenExportModal = () => {
		setIsOpen(false);
		setOpenExportModal(true);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchDisciplinaryActions}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Action Number",
										fieldName: "actionNumber",
									},
									{
										title: "Action Date",
										fieldName: "actionDate",
									},
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
									{
										title: "Date Modified",
										fieldName: "modifiedAt",
									},
								],
							},
							{
								title: "Action Type",
								fieldName: "actionTypeId",
								fieldType: "SELECT_FIELD",
								options: disciplinaryActionTypes?.data || [],
							},
							{
								title: "Employee",
								fieldName: "employeeId",
								fieldType: "SELECT_FIELD",
								options: employees?.data || [],
								mapTexts: ["firstName", "lastName"],
							},
							{
								title: "Action Date",
								fieldName: "actionDate",
								fieldType: "DATE_RANGE",
							},
						]}
					/>
					<ManageTableColumns
						columns={disciplinaryActionsColumns}
						defaultVisibilityModel={disciplinaryActionsColumnVisibility}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						minVisibleColumns={7}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<div className="flex items-center mx-1">
					<div className="relative" ref={dropdownRef}>
						{/* Dropdown Trigger Button */}
						<button
							onClick={() => setIsOpen(!isOpen)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<Upload className="mr-3 text-[17px] text-gray-500" />
							Upload/Export
							<ChevronDown
								className={`ml-2 text-[14px] text-gray-500 transition-transform duration-200 ${
									isOpen ? "rotate-180" : ""
								}`}
							/>
						</button>

						{/* Dropdown Menu */}
						{isOpen && (
							<div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-200 rounded-xl shadow-lg z-50">
								<div className="p-2 space-y-2">
									{/* <PermissionChecker
														permissionRule="OR"
														permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
													>
														<button
															onClick={() => setOpenUploadSalaryAdvancesModal(true)}
															className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
																	   border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
														>
															<GoUpload className="text-[18px] text-gray-500" />
															Upload Salary Advance
														</button>
													</PermissionChecker> */}
									<button
										onClick={handleOpenExportModal}
										className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
																	 border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
									>
										<BsDownload className="text-[18px] text-gray-500" />
										Export Disciplinary Actions
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
				<PermissionChecker
					permissionRule="OR"
					permissions={[PERMISSIONS.COMPANY_CONFIGS_CONDUCT_WRITE]}
				>
					<div
						className="flex items-center text-primary-blue cursor-pointer"
						onClick={canManageConduct ? handleOpenModal : undefined}
					>
						<AiOutlinePlus className="mr-1 text-base" />
						<span className="text-sm">Add New Disciplinary Action</span>
					</div>
				</PermissionChecker>
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.DISCIPLINARY}
				data={tableData}
				columns={disciplinaryActionsColumns}
				columnVisibility={columnVisibilityModel} // ✅ Add this
				onColumnVisibilityChange={setColumnVisibilityModel} // ✅ Add this
				loading={isLoading}
				// Rows aren't clickable (no pointer cursor) for non-managers
				conditionalRowAction={() => canManageConduct}
				onRowClick={(row, event) => {
					if (!canManageConduct) return;
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					if (!canManageConduct) return;
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={disciplinaryActionsColumnVisibility}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				key={tableKey}
				rows={tableData}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				columnVisibilityModel={columnVisibilityModel}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={setAnchorEl}
			/> */}
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal && canManageConduct}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && canManageConduct && (
				<DisciplinaryModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={
						isEditForm
							? "Update Disciplinary Action"
							: "New Disciplinary Action"
					}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					employees={employees?.data || []}
					actionTypes={disciplinaryActionTypes?.data || []}
					grievanceReports={grievanceReports?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openExportModal && currentCompany?.id && (
				<ExportDisciplinaryActionsModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</>
	);
};

export default DisciplinaryActionsView;
